import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Draggable } from 'react-beautiful-dnd';
import { Button, Checkbox, Icon, Modal } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../../lib/popup';
import { Popup } from '../../../lib/custom-ui';

import NameEdit from './NameEdit';
import ActionsStep from './ActionsStep';
import TaskDueDateStep from '../../TaskDueDateStep';
import CardReminderStep from '../../CardReminderStep/CardReminderStep';

import styles from './Item.module.scss';
import modalStyles from '../CardModal.module.scss';

const Item = React.memo(
  ({
    id,
    index,
    name,
    isCompleted,
    isPersisted,
    dueDate,
    reminderEnabled,
    reminderDate,
    canEdit,
    onUpdate,
    onDelete,
  }) => {
    const [t] = useTranslation();
    const nameEdit = useRef(null);
    const [isDueDateModalOpen, setIsDueDateModalOpen] = useState(false);
    const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const handleClick = useCallback(() => {
      if (isPersisted && canEdit) {
        nameEdit.current.open();
      }
    }, [isPersisted, canEdit]);

    const handleNameUpdate = useCallback(
      (newName) => {
        onUpdate({
          name: newName,
        });
      },
      [onUpdate],
    );

    const handleToggleChange = useCallback(() => {
      onUpdate({
        isCompleted: !isCompleted,
      });
    }, [isCompleted, onUpdate]);

    const handleNameEdit = useCallback(() => {
      nameEdit.current.open();
    }, []);

    const handleDueDateClick = useCallback(() => {
      if (canEdit && isPersisted) {
        setIsDueDateModalOpen(true);
      }
    }, [canEdit, isPersisted]);

    const handleDueDateApply = useCallback(
      (date) => {
        onUpdate({
          dueDate: date ? date.toISOString() : null,
        });
        setIsDueDateModalOpen(false);
      },
      [onUpdate],
    );

    const handleDueDateClose = useCallback(() => {
      setIsDueDateModalOpen(false);
    }, []);

    const handleReminderClick = useCallback(() => {
      if (canEdit && isPersisted && dueDate) {
        setIsReminderModalOpen(true);
      }
    }, [canEdit, isPersisted, dueDate]);

    const handleReminderApply = useCallback(
      ({ date, enabled }) => {
        onUpdate({
          reminderDate: date ? date.toISOString() : null,
          reminderEnabled: Boolean(enabled),
        });
        setIsReminderModalOpen(false);
      },
      [onUpdate],
    );

    const handleReminderClose = useCallback(() => {
      setIsReminderModalOpen(false);
    }, []);

    const dueDateColor = useMemo(() => {
      if (!dueDate) {
        return null;
      }
      const dueDateObj = new Date(dueDate);
      const now = new Date();
      const diffDays = Math.floor((dueDateObj - now) / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        return 'red'; // Просрочено
      } else if (diffDays <= 5) {
        return 'orange'; // Осталось 5 дней или меньше
      } else {
        return 'green'; // Более 5 дней
      }
    }, [dueDate]);

    const dueDateText = useMemo(() => {
      if (!dueDate) {
        return 'Срок';
      }
      const dueDateObj = new Date(dueDate);
      const formatted = t('format:dateTime', {
        postProcess: 'formatDate',
        value: dueDateObj,
      });
      return `Срок подзадачи: ${formatted}`;
    }, [dueDate, t]);

    const reminderButtonContent = useMemo(() => {
      if (!dueDate) {
        return t('common.selectTaskDueDateFirst', { defaultValue: 'Сначала выберите срок подзадачи' });
      }
      if (reminderDate) {
        const formatted = t('format:dateTime', {
          postProcess: 'formatDate',
          value: new Date(reminderDate),
        });

        return `${t('Item_reminderButton', { defaultValue: 'Напоминание' })}: ${formatted}`;
      }

      return t('common.setReminder', { defaultValue: 'Установить напоминание' });
    }, [dueDate, reminderEnabled, reminderDate, t]);

    const isReminderButtonDisabled = useMemo(
      () => !dueDate || !isPersisted || !canEdit,
      [dueDate, isPersisted, canEdit],
    );

    const ActionsPopup = usePopup(ActionsStep);

    const handleNameEditOpen = useCallback(() => {
      setIsEditing(true);
    }, []);

    const handleNameEditClose = useCallback(() => {
      setIsEditing(false);
    }, []);

    return (
      <>
      <Draggable draggableId={id} index={index} isDragDisabled={!isPersisted || !canEdit}>
        {({ innerRef, draggableProps, dragHandleProps }, { isDragging }) => {
          const contentNode = (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <div {...draggableProps} {...dragHandleProps} ref={innerRef} className={styles.wrapper}>
              <span className={styles.checkboxWrapper}>
                <Checkbox
                  checked={isCompleted}
                  disabled={!isPersisted || !canEdit}
                  className={styles.checkbox}
                  onChange={handleToggleChange}
                />
              </span>
              <NameEdit 
                ref={nameEdit} 
                defaultValue={name} 
                onUpdate={handleNameUpdate}
                onOpen={handleNameEditOpen}
                onClose={handleNameEditClose}
              >
                <div className={classNames(canEdit && styles.contentHoverable)}>
                  <span
                    className={classNames(styles.text, canEdit && styles.textEditable)}
                    onClick={handleClick}
                  >
                    <span className={classNames(styles.task, isCompleted && styles.taskCompleted)}>
                      {name}
                    </span>
                  </span>
                  {isPersisted && canEdit && !isEditing && (
                    <ActionsPopup onNameEdit={handleNameEdit} onDelete={onDelete}>
                      <Button className={classNames(styles.button, styles.target)}>
                        <Icon fitted name="pencil" size="small" />
                      </Button>
                    </ActionsPopup>
                  )}
                </div>
              </NameEdit>
              {isPersisted && canEdit && !isEditing && (
                <div className={styles.actionsRow}>
                  <button
                    type="button"
                    className={classNames(
                      styles.dueDateButton,
                      dueDateColor === 'red' && styles.dueDateButtonRed,
                      dueDateColor === 'orange' && styles.dueDateButtonOrange,
                      dueDateColor === 'green' && styles.dueDateButtonGreen,
                    )}
                    onClick={handleDueDateClick}
                  >
                    {dueDateText}
                  </button>
                  <Popup
                    content={reminderButtonContent}
                    position="top center"
                    inverted
                    trigger={
                      <span>
                        <button
                          type="button"
                          className={classNames(
                            styles.reminderButton,
                            isReminderButtonDisabled && styles.reminderButtonDisabled,
                            reminderEnabled && reminderDate && styles.reminderButtonActive,
                          )}
                          onClick={handleReminderClick}
                          disabled={isReminderButtonDisabled}
                        >
                          <Icon name="clock outline" />
                        </button>
                      </span>
                    }
                  />
                </div>
              )}
            </div>
          );

          return isDragging ? ReactDOM.createPortal(contentNode, document.body) : contentNode;
        }}
      </Draggable>
      {isDueDateModalOpen && (
        <Modal
          open={isDueDateModalOpen}
          onClose={handleDueDateClose}
          size="small"
          centered={false}
          closeIcon
          className={modalStyles.taskDueDateModal}
        >
          <TaskDueDateStep
            defaultValue={dueDate ? new Date(dueDate) : undefined}
            onApply={handleDueDateApply}
            onClose={handleDueDateClose}
          />
        </Modal>
      )}
      {isReminderModalOpen && (
        <Modal
          open={isReminderModalOpen}
          onClose={handleReminderClose}
          size="small"
          centered={false}
          closeIcon
          className={modalStyles.taskDueDateModal}
        >
          <CardReminderStep
            defaultValue={reminderDate ? new Date(reminderDate) : undefined}
            defaultEnabled={reminderEnabled}
            onApply={handleReminderApply}
            onClose={handleReminderClose}
          />
        </Modal>
      )}
    </>
    );
  },
);

Item.propTypes = {
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  isPersisted: PropTypes.bool.isRequired,
  dueDate: PropTypes.string,
  reminderEnabled: PropTypes.bool,
  reminderDate: PropTypes.string,
  canEdit: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

Item.defaultProps = {
  dueDate: undefined,
  reminderEnabled: false,
  reminderDate: undefined,
};

export default Item;
